/** Utilities for identifying the next actionable subtask in a project branch. */

/**
 * Bepaalt de status van een taak op basis van zijn eigenschappen.
 * De "waiting" status (gedelegeerd) heeft voorrang op de "planned" status.
 *
 * @param {import('@/types').Task} task - De taak om de status voor te bepalen.
 * @returns {'planned' | 'waiting' | 'default'}
 */
export const getNextActionState = (task) => {
  if (!task) {
    return 'default';
  }
  if (task.person) {
    return 'waiting';
  }
  if (task.date) {
    return 'planned';
  }
  return 'default';
};

/**
 * Recursively finds the deepest, first uncompleted and non-backlogged task in the subtree.
 * Ignores tasks with status 'Backlog'.
 *
 * Guards against circular references in the task hierarchy by tracking visited task IDs.
 * If a cycle is detected, the branch returns null.
 *
 * @param {import('@/types').Task} task - The task to start the search from.
 * @param {Set<string>} [visited] - Set of already-visited task IDs (cycle detection).
 * @returns {import('@/types').Task | null} The deepest valid task, or null if none is found.
 */
export const findDeepestUncompletedTask = (task, visited = new Set()) => {
  // Guard against circular references.
  if (visited.has(task.id)) {
    return null;
  }
  visited.add(task.id);

  // Find a child that is not completed and not on the backlog.
  const firstUncompletedChild = task.children?.find(
    (child) => !child.completed && child.status !== 'Backlog',
  );

  if (firstUncompletedChild) {
    // Go deeper if a valid child is found.
    return findDeepestUncompletedTask(firstUncompletedChild, visited);
  }

  // This is the deepest task in this branch. Check that it is not on the backlog itself.
  if (task.status === 'Backlog') {
    return null;
  }

  // Otherwise this is a valid "next action".
  return task;
};

const getEligibleChild = (task) => {
  return task.children?.find(
    (child) => !child.completed && child.status !== 'Backlog',
  );
};

const processProjectWithChildren = (
  task,
  firstUncompletedChild,
  nextActionMap,
) => {
  const nextAction = findDeepestUncompletedTask(firstUncompletedChild);

  if (nextAction) {
    const state = getNextActionState(nextAction);
    nextActionMap.set(task.id, { nextAction, state });
  }
};

const processStandaloneTask = (task, nextActionMap) => {
  if (task.status !== 'Backlog') {
    const state = getNextActionState(task);
    nextActionMap.set(task.id, { nextAction: task, state });
  }
};

export const processRootTasks = (rootTasks) => {
  const nextActionMap = new Map();

  for (const task of rootTasks) {
    if (task.completed) continue;

    const firstUncompletedChild = getEligibleChild(task);

    if (firstUncompletedChild) {
      processProjectWithChildren(task, firstUncompletedChild, nextActionMap);
    } else {
      processStandaloneTask(task, nextActionMap);
    }
  }

  return nextActionMap;
};

/**
 * Filters and enriches the next actions from the map, returning a sorted list.
 *
 * @param {Map<string, import('@/types').Task>} tasksMap
 * @param {Map<string, {nextAction: Object, state: string}>} nextActionMap
 * @param {Set<string>} filteredTaskIds
 * @returns {Array<Object>}
 */
export const getNextActionsList = (
  tasksMap,
  nextActionMap,
  filteredTaskIds,
) => {
  const list = [];
  for (const [projectId, { nextAction, state }] of nextActionMap.entries()) {
    const isActionable =
      state === 'default' &&
      !nextAction.completed &&
      nextAction.status !== 'Backlog' &&
      filteredTaskIds.has(nextAction.id);
    if (!isActionable) continue;

    const parent = tasksMap.get(projectId);
    if (parent && !parent.completed) {
      list.push({
        ...nextAction,
        sortBreadcrumb: { id: parent.id, title: parent.title },
      });
    }
  }
  return list.sort((a, b) => {
    const pathA = a.materializedPath || '';
    const pathB = b.materializedPath || '';
    return pathA < pathB ? -1 : pathA > pathB ? 1 : 0;
  });
};

/**
 * Plan-only gate for the Next Action rail/tab: true once a parent/subtask
 * hierarchy exists. On Plan, standalone undated tasks already appear in
 * "To schedule", so the panel stays hidden until there are subtasks.
 * Today uses `nextActions.length > 0` instead (see TodayViewPanels).
 *
 * @param {Iterable<import('@/types').Task>|Map<string, import('@/types').Task>|null|undefined} tasks
 * @returns {boolean}
 */
export const shouldShowNextActionPanel = (tasks) => {
  if (!tasks) return false;
  const list = tasks instanceof Map ? tasks.values() : tasks;
  for (const task of list) {
    if (!task) continue;
    if (task.parentId) return true;
    if (Array.isArray(task.children) && task.children.length > 0) return true;
  }
  return false;
};
